import { makeAutoObservable } from 'mobx';
import { ApportionmentApi, ApportionmentApiResponse } from '../../../api/ApportionmentApi';
import { ApportionmentAssignmentAction, ApportionmentType, ApportionmentUnit, ApportionmentVisibility } from '../../../config/Constants';
import { Split } from './Split';
import { Assignment } from './Assignment';
import { NegativeIdGenerator } from '../../../utils/NegativeIdGenerator';
import { AssignmentSetting } from './AssignmentSetting';

export type OverrideRequirements = {
  nominalCode: boolean,
  trackingCategories: Map<string, boolean>
};

export class Apportionment {
  id: number = 0;
  groupId: number = 0;
  name: string = '';
  type: ApportionmentType = ApportionmentType.CROSS_ENTITY;
  visibility: ApportionmentVisibility = ApportionmentVisibility.GROUP;
  localTotal: number = 0;
  units: ApportionmentUnit = ApportionmentUnit.UNIT;
  splits: Split[] = [];
  errored: boolean = false;
  isDeletable: boolean = false;

  totalError: boolean = false;
  nameError: boolean = false;

  // This temporarily holds the total in units for when the user switches between percentage and units
  unitsTotal: number = -1;

  nominalCodeAction: ApportionmentAssignmentAction = ApportionmentAssignmentAction.RETAIN;
  trackingCategoryActions: Map<string, ApportionmentAssignmentAction> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(apiData: ApportionmentApiResponse, groupId: number): Apportionment {
    const appt = new Apportionment();
    appt.parseApi(apiData, groupId);
    return appt;
  }

  parseApi(apiData: ApportionmentApiResponse, groupId: number) {
    if (apiData) {
      this.id = apiData.id;
      this.groupId = groupId;
      this.name = apiData.name;
      this.type = apiData.type as ApportionmentType;
      this.visibility = apiData.visibility as ApportionmentVisibility;
      this.localTotal = apiData.total;
      this.units = apiData.splitsUnit as ApportionmentUnit;
      this.isDeletable = apiData.deletable;
      this.nominalCodeAction = apiData.accountCodeAction as ApportionmentAssignmentAction || ApportionmentAssignmentAction.SET;
      this.trackingCategoryActions = new Map();
      
      Object.keys(apiData.trackingCategoryAction || {}).forEach(key =>
        this.trackingCategoryActions.set(key, apiData.trackingCategoryAction[key] as ApportionmentAssignmentAction)
      );
  
      this.splits = apiData.splits.map(
        split => Split.fromApi(split, groupId)
      ); 
    }
  }

  static intraEntityApportionment(groupId: number, entityId: number, assignment: Assignment = null, assignmentSettings: AssignmentSetting = null): Apportionment {
    const apportionment = new Apportionment();
    apportionment.id = NegativeIdGenerator.next();
    apportionment.groupId = groupId;
    apportionment.type = ApportionmentType.INTRA_ENTITY;
    apportionment.visibility = ApportionmentVisibility.RULE;
    apportionment.nominalCodeAction = ApportionmentAssignmentAction.SET;

    const newSplit = new Split();
    newSplit.split = 1;

    if (assignment) {
      newSplit.subApportionment = assignment;
    } else {
      const newAssignment = Assignment.fromAssignmentSettings(entityId, assignmentSettings);
      newSplit.subApportionment = newAssignment;
    }

    apportionment.splits = [newSplit];
    apportionment.units = ApportionmentUnit.UNIT;
    apportionment.total = 1;

    return apportionment;
  }

  toApi(): ApportionmentApiResponse {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      total: this.total,
      visibility: this.visibility,
      splitsUnit: this.units,
      splits: this.splits.map(entry => entry.toApi()),
      accountCodeAction: this.nominalCodeAction,
      trackingCategoryAction: Object.fromEntries(this.trackingCategoryActions),
    };
  }

  get isTotalEditable(): boolean {
    return this.units !== ApportionmentUnit.PERCENTAGE;
  }

  get total(): number {
    return this.units === ApportionmentUnit.PERCENTAGE
      ? 100
      : Math.max(
        this.localTotal,
        this.apportionmentSum
      );
  }

  set total(total: number) {
    this.localTotal = total;
  }

  get apportionmentSum(): number {
    return +(this.splits.map(e => e.split).reduce((a, b) => a + b, 0)).toFixed(12);
  }

  get hasBeenPersisted(): boolean {
    return this.id > 0;
  }

  get isValid(): boolean {
    this.nameError = this.name === '';
    this.totalError = this.apportionmentSum > this.total || this.apportionmentSum === 0;

    let sumError = false;
    if (this.units === ApportionmentUnit.PERCENTAGE) {
      sumError = this.apportionmentSum !== 100;
      this.splits.forEach(split => split.valueError = sumError);
    }

    let codeError = false;
    if (this.nominalCodeAction === ApportionmentAssignmentAction.SET && this.type === ApportionmentType.INTRA_ENTITY) {
      this.splits.forEach(split => {
        const assignment = split.subApportionment as Assignment;
        if (!assignment.nominalCode?.externalId || assignment.nominalCode.externalId === '') {
          assignment.codeError = true;
          codeError = true;
        }
      });
    }

    // A single split with all retain is pointless
    if (this.type === ApportionmentType.INTRA_ENTITY && this.splits.length === 1) {
      const actions = new Set([
        this.nominalCodeAction,
        ...this.trackingCategoryActions.values(),
      ]);

      if (actions.size === 1 && [...actions.values()][0] === ApportionmentAssignmentAction.RETAIN) {
        (this.splits[0].subApportionment as Assignment).codeError = true;
        (this.splits[0].subApportionment as Assignment).categoryError = true;
        codeError = true;
      }
    }

    return !(this.nameError || this.totalError || sumError || codeError);
  }

  addAssignmentSplit(entityId: number, value: number): Split {
    const split = new Split();
    split.split = value;

    const assignment = new Assignment();
    assignment.id = NegativeIdGenerator.next();
    assignment.entityId = entityId;
    split.subApportionment = assignment;

    this.splits.push(split);

    return split;
  }

  splitForEntity(entityId: number): Split {
    return this.splits.find(s => (s.subApportionment.type === ApportionmentType.ASSIGNMENT && s.subApportionment.entityId === entityId));
  }

  entities(assignmentSettings: Map<number, AssignmentSetting>): number[] {
    const ids: Set<number> = new Set();

    this.splits
      .filter(split => split.split > 0)
      .forEach(split => {
        if (assignmentSettings.has(split.siblingId) && assignmentSettings.get(split.siblingId).ignore) return;

        if (split.subApportionment.type === ApportionmentType.ASSIGNMENT) {
          ids.add(split.subApportionment.entityId);
        } else {
          (split.subApportionment as Apportionment).entities(assignmentSettings)
            .forEach(id => ids.add(id));
        }
      });

    return [...ids.values()];
  }

  assignmentIds(): number[] {
    const ids: Set<number> = new Set();

    this.splits
      .filter(split => split.split > 0)
      .forEach(split => {
        if (split.subApportionment.type === ApportionmentType.ASSIGNMENT) {
          ids.add(split.siblingId);
        } else {
          ids.add(split.siblingId); // Add the child ID in case we've overridden it
          (split.subApportionment as Apportionment).assignmentIds()
            .forEach(id => ids.add(id));
        }
      });

    return [...ids.values()];
  }

  get entityId(): number {
    if (this.type === ApportionmentType.CROSS_ENTITY) {
      return null;
    }
    return this.entities(new Map())[0];
  }

  save = (): Promise<void> => {
    const promise = this.id > 0
      ? ApportionmentApi.updateApportionment(this.groupId, this.id, this.toApi())
      : ApportionmentApi.createApportionment(this.groupId, this.toApi());

    return promise
      .then(response => {
        this.parseApi(response, this.groupId);
      });
  };

  changeUnits() {
    if (this.units === ApportionmentUnit.PERCENTAGE) {
      if (this.unitsTotal > 0) {
        this.splits.forEach(split => {
          split.split = (split.split * this.unitsTotal) / 100;
        });
        this.total = this.unitsTotal;
      } else {
        this.total = this.apportionmentSum;
      }

      this.units = ApportionmentUnit.UNIT;
    } else {
      this.unitsTotal = this.total;

      this.splits.forEach(split => {
        split.split = 100 * (split.split / this.unitsTotal);
      });

      this.total = 100;
      this.units = ApportionmentUnit.PERCENTAGE;
    }
  }
}
